/**
 * t513u/t514u: Settings > Users > Edit details (name, email, phone) and Resend invite. The rules the web, phone web and
 * API share: what is checked, which fields changed, the toast wording, and the muted "Invited 3 days ago" line.
 */
import type { MemberInvitation } from './invitations';

export interface MemberDetailsInput { display_name?: string; email?: string; phone?: string | null }
export interface MemberDetails { display_name: string; email: string | null; phone: string | null }
export interface MemberDetailsResult extends MemberDetails {
  user_id: string;
  changed: (keyof MemberDetails)[];
  /** The sign-in email changed (Supabase Auth updated too). */
  email_changed: boolean;
  /** The person had an open invite, so it was sent again to the new address. */
  invite_resent: boolean;
  /** Who was told about the email change (the old address), when an email went out. */
  notified: string | null;
  message: string;
}

export const USER_DETAILS_LIMITS = { name: 120, email: 320, phone: 40 } as const;
export const RESEND_INVITE_LABEL = 'Resend invite';
/** One resend per person per minute (the API checks again). */
export const RESEND_INVITE_COOLDOWN_SECONDS = 60;
export const RESEND_INVITE_TOO_SOON = 'An invite was sent a moment ago. Give it a minute before sending another.';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const normaliseEmail = (e: string) => e.trim().toLowerCase();
const clean = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();

/** A plain sentence for the first problem, or null when the details can be saved. */
export function userDetailsProblem(d: { display_name: string; email: string; phone: string; had_email?: boolean }): string | null {
  const name = clean(d.display_name), email = normaliseEmail(d.email), phone = clean(d.phone);
  if (!name) return 'Add their name';
  if (name.length > USER_DETAILS_LIMITS.name) return `Keep the name to ${USER_DETAILS_LIMITS.name} characters`;
  if (!email && d.had_email) return 'Add their email: it’s how they sign in';
  if (email && (email.length > USER_DETAILS_LIMITS.email || !EMAIL.test(email))) return 'That email doesn’t look right';
  if (phone.length > USER_DETAILS_LIMITS.phone) return `Keep the phone number to ${USER_DETAILS_LIMITS.phone} characters`;
  if (phone && !/^[+()\d\s.-]{3,}$/.test(phone)) return 'Use digits, spaces and + ( ) - in the phone number';
  return null;
}

/** Only what changed, ready to send (empty = nothing to save). */
export function userDetailsPatch(before: MemberDetails, form: { display_name: string; email: string; phone: string }): MemberDetailsInput {
  const out: MemberDetailsInput = {};
  const name = clean(form.display_name), email = normaliseEmail(form.email), phone = clean(form.phone) || null;
  if (name !== before.display_name) out.display_name = name;
  if (email && email !== normaliseEmail(before.email ?? '')) out.email = email;
  if (phone !== (clean(before.phone) || null)) out.phone = phone;
  return out;
}

/** Open invitations can be resent; accepted and revoked ones can't (the person has joined, or it was withdrawn). */
export const canResendInvite = (i: Pick<MemberInvitation, 'status'> | null | undefined) => !!i && (i.status === 'pending' || i.status === 'emailed' || i.status === 'expired');

/** The person's open invitation, matched by account or (before the link was made) by email. */
export function inviteFor<I extends Pick<MemberInvitation, 'status' | 'user_id' | 'email'>>(list: I[] | undefined, userId: string, email?: string | null): I | null {
  const open = (list ?? []).filter(canResendInvite);
  return open.find(i => i.user_id === userId) ?? (email ? open.find(i => i.email.toLowerCase() === email.toLowerCase()) : undefined) ?? null;
}

/** "Invited 3 days ago" from the last email (or when it was saved). */
export function invitedAgo(i: Pick<MemberInvitation, 'created_at' | 'last_sent_at'>, now = new Date()): string {
  const at = new Date(i.last_sent_at ?? i.created_at).getTime();
  const mins = Math.max(0, Math.floor((now.getTime() - at) / 60_000));
  const verb = i.last_sent_at ? 'Invited' : 'Invite saved';
  if (mins < 1) return `${verb} just now`;
  if (mins < 60) return `${verb} ${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${verb} ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${verb} ${days} day${days === 1 ? '' : 's'} ago`;
}

export const inviteSentToast = (email: string) => `Invite sent to ${email}`;
